import {
  createStyles,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  addHours,
  addSeconds,
  differenceInHours,
  differenceInMilliseconds,
  differenceInMinutes,
  differenceInSeconds,
  parse,
} from 'date-fns';
import { addMinutes } from 'date-fns/esm';
import translation from 'locales/translation';
import { useCallback, useEffect, useState } from 'react';

export interface IInputTimeObject {
  timeStr: string;
  timeInMiliseconds: number;
  timeInSeconds: number;
  timeInMinutes: number;
  timeInHours: number;
}

export interface IInputTime {
  value: string;
  duration?: boolean;
  onChange: (time: IInputTimeObject) => void;
  className?: string;
}

const useStyles = makeStyles(() =>
  createStyles({
    wrapper: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-end',
    },
    separator: {
      marginBottom: '6px',
    },
    textFieldRoot: {
      flex: 1,
    },
    textFieldLabel: {
      textAlign: 'center',
    },
    input: {
      textAlign: 'center',
    },
  })
);

export default function InputTime({
  value,
  duration,
  onChange,
  className,
}: IInputTime) {
  const classes = useStyles();
  const newValue = value.split(':').length === 3 ? value : '0:0:0';
  const [hours, setHours] = useState<number>(Number(newValue.split(':')[0]));
  const [minutes, setMinutes] = useState<number>(
    Number(newValue.split(':')[1])
  );
  const [seconds, setSeconds] = useState<number>(
    Number(newValue.split(':')[2])
  );

  const getParsedValue = useCallback((): IInputTimeObject => {
    const date = new Date(2020, 0, 1, 0, 0, 0, 0);
    const refrenceDate = parse('00:00:00', 'HHH:mm:ss', date);
    const dateSeconds = addSeconds(date, seconds);
    const dateMinutes = addMinutes(dateSeconds, minutes);
    const dateHours = addHours(dateMinutes, hours);
    return {
      timeStr:
        hours.toString() + ':' + minutes.toString() + ':' + seconds.toString(),
      timeInMiliseconds: differenceInMilliseconds(dateHours, refrenceDate),
      timeInSeconds: differenceInSeconds(dateHours, refrenceDate),
      timeInMinutes: differenceInMinutes(dateHours, refrenceDate),
      timeInHours: differenceInHours(dateHours, refrenceDate),
    };
  }, [hours, minutes, seconds]);

  useEffect(() => {
    onChange(getParsedValue());
    // eslint-disable-next-line
  }, [hours, minutes, seconds]);

  const getHoursByInputValue = useCallback(
    (n: number): number => {
      if (duration) {
        return n > 999 ? 999 : n;
      }
      return n > 23 ? 23 : n;
    },
    [duration]
  );

  const getMinutesByInputValue = (n: number): number => {
    return n > 59 ? 59 : n;
  };

  const getSecondsByInputValue = (n: number): number => {
    return n > 59 ? 59 : n;
  };

  return (
    <div className={classes.wrapper + ' ' + (className || '')}>
      <TextField
        name='hours'
        type='number'
        label={translation.InputTime.hoursLabel}
        className={classes.textFieldRoot}
        inputProps={{
          min: 0,
          max: duration ? 999 : 23,
          className: classes.input,
        }}
        InputLabelProps={{
          className: classes.textFieldLabel,
        }}
        value={hours > 0 ? hours.toString().replace(/^0+/, '') : 0}
        onChange={(event) => {
          setHours(getHoursByInputValue(Number(event.target.value)));
        }}
      />
      <Typography className={classes.separator}>:</Typography>
      <TextField
        name='minutes'
        type='number'
        label={translation.InputTime.minutesLabel}
        className={classes.textFieldRoot}
        inputProps={{
          min: 0,
          max: 59,
          className: classes.input,
        }}
        InputLabelProps={{
          className: classes.textFieldLabel,
        }}
        value={minutes > 0 ? minutes.toString().replace(/^0+/, '') : 0}
        onChange={(event) =>
          setMinutes(getMinutesByInputValue(Number(event.target.value)))
        }
      />
      <Typography className={classes.separator}>:</Typography>
      <TextField
        name='seconds'
        type='number'
        label={translation.InputTime.secondsLabel}
        className={classes.textFieldRoot}
        inputProps={{
          min: 0,
          max: 59,
          className: classes.input,
        }}
        InputLabelProps={{
          className: classes.textFieldLabel,
        }}
        value={seconds > 0 ? seconds.toString().replace(/^0+/, '') : 0}
        onChange={(event) =>
          setSeconds(getSecondsByInputValue(Number(event.target.value)))
        }
      />
    </div>
  );
}
